import React, { useState } from 'react'
import {
  Formik,
  Field,
  Form,
  FormikHelpers,
  FormikProps,
  ErrorMessage
} from 'formik'
import * as Yup from 'yup'
import Select from 'components/features/common/select/Select'
import Button from 'react-bootstrap/Button'
import { RootState } from 'app/rootReducer'
import { useSelector, useDispatch } from 'react-redux'
import { dropdownStateOptions, phoneRegExp } from 'share/utils/utils'
import { Authentication } from 'share/domain/authentication'
import {
  UserProfile,
  getEmptyUserProfile,
  setUserProfile
} from 'reducers/userprofile/userprofileSlice'
import {
  UpdateAccountResponseData,
  UpdateAccountVariables
} from 'share/mutations/account'
import './styles.scss'
import {
  getContactTypeValueFromArray,
  getInitialContactTypeArrayValue
} from 'share/domain/account'
import { ButtonsContainer } from 'components/features/dashboard/questionnaire/QuestionnareForm/styles'
import { TwoBtnsContainer } from 'components/atoms/Layout'

interface Props {
  authentication: Authentication
  nextPage(page: string): void
  previousPage(page: string): void
  updateAccountHelper(
    input: UpdateAccountVariables
  ): Promise<UpdateAccountResponseData>
  submitErrorMessage: string | null
  setSubmitErrorMessage: React.Dispatch<React.SetStateAction<string | null>>
}

interface Values {
  firstName: string
  lastName: string
  middleName: string
  suffix: string
  homePhone: string
  mobilePhone: string
  workPhone: string
  emailAddress: string
  mailingAddress: string
  zipCode: string
  city: string
  selectState: string
  password: string
  contactType: string[]
}

function getEmptyInitialValues(): Values {
  const initialValues: Values = {
    firstName: '',
    lastName: '',
    middleName: '',
    suffix: '',
    homePhone: '',
    mobilePhone: '',
    workPhone: '',
    emailAddress: '',
    mailingAddress: '',
    zipCode: '',
    city: '',
    selectState: '',
    password: '123456789',
    contactType: ['Email']
  }
  return initialValues
}

const validationSchema = Yup.object({
  firstName: Yup.string().required('First Name is Required'),
  lastName: Yup.string().required('Last Name is Required'),
  middleName: Yup.string(),
  suffix: Yup.string(),
  homePhone: Yup.string().matches(
    phoneRegExp,
    'Home phone number is not valid'
  ),
  mobilePhone: Yup.string().matches(
    phoneRegExp,
    'Mobile phone number is not valid'
  ),
  workPhone: Yup.string().matches(
    phoneRegExp,
    'Work phone number is not valid'
  ),
  emailAddress: Yup.string().email('Invalid email format').required('Required'),
  mailingAddress: Yup.string(),
  zipCode: Yup.string(),
  city: Yup.string(),
  selectState: Yup.string().required('State selection required'),
  contactType: Yup.array()
    .min(1, 'Please select an option.')
    .required('Required')
})

const UserProfileEditForm = (props: Props): JSX.Element => {
  const {
    authentication,
    nextPage,
    previousPage,
    updateAccountHelper,
    submitErrorMessage,
    setSubmitErrorMessage
  } = props

  const dispatch = useDispatch()
  const userprofile = useSelector((state: RootState) => state.userprofile)
  const [loading, setLoading] = useState(true)
  const [formIsDirty, setFormIsDirty] = useState(false)
  const [initialVals, setInitialVals] = useState<Values>(() =>
    getEmptyInitialValues()
  )

  React.useEffect(() => {
    console.log('entered useEffect')
    const initial = setInitialValues(authentication, userprofile)
    setInitialVals(initial)

    setLoading(false)
    console.log('use effect initial', initial)
  }, [])

  function setInitialValues(
    authentication: Authentication,
    userprofile: UserProfile
  ): Values {
    const initialValues: Values = {
      firstName:
        authentication.account && authentication.account.givenName
          ? authentication.account.givenName
          : '',
      lastName:
        authentication.account && authentication.account.familyName
          ? authentication.account.familyName
          : '',
      middleName:
        authentication.account && authentication.account.middleName
          ? authentication.account.middleName
          : '',
      suffix:
        authentication.account && authentication.account.suffix
          ? authentication.account.suffix
          : '',
      homePhone:
        authentication.account && authentication.account.homePhone
          ? authentication.account.homePhone
          : '',
      mobilePhone:
        authentication.account && authentication.account.mobilePhone
          ? authentication.account.mobilePhone
          : '',
      workPhone:
        authentication.account && authentication.account.workPhone
          ? authentication.account.workPhone
          : '',
      emailAddress:
        authentication.account && authentication.account.emailAddress
          ? authentication.account.emailAddress
          : '',
      mailingAddress:
        authentication.account && authentication.account.street
          ? authentication.account.street
          : '',
      zipCode:
        authentication.account && authentication.account.zip
          ? authentication.account.zip
          : '',
      city:
        authentication.account && authentication.account.city
          ? authentication.account.city
          : '',
      selectState:
        authentication.account && authentication.account.state
          ? authentication.account.state
          : '',
      password: '123456789',
      contactType: getInitialContactTypeArrayValue(
        authentication.account && authentication.account.contactType
          ? authentication.account.contactType
          : 0
      )
    }

    if (userprofile.isActive) {
      if (userprofile.givenName) {
        initialValues.firstName = userprofile.givenName
      }
      if (userprofile.familyName) {
        initialValues.lastName = userprofile.familyName
      }
      if (userprofile.middleName) {
        initialValues.middleName = userprofile.middleName
      }
      if (userprofile.suffix) {
        initialValues.suffix = userprofile.suffix
      }
      if (userprofile.homePhone) {
        initialValues.homePhone = userprofile.homePhone
      }
      if (userprofile.mobilePhone) {
        initialValues.mobilePhone = userprofile.mobilePhone
      }
      if (userprofile.workPhone) {
        initialValues.workPhone = userprofile.workPhone
      }
      if (userprofile.emailAddress) {
        initialValues.emailAddress = userprofile.emailAddress
      }
      if (userprofile.street) {
        initialValues.mailingAddress = userprofile.street
      }
      if (userprofile.zip) {
        initialValues.zipCode = userprofile.zip
      }
      if (userprofile.city) {
        initialValues.city = userprofile.city
      }
      if (userprofile.state) {
        initialValues.selectState = userprofile.state
      }

      if (userprofile.contactType) {
        initialValues.contactType = getInitialContactTypeArrayValue(
          userprofile.contactType
        )
      }

      setFormIsDirty(true)
    }

    return initialValues
  }

  function onSubmitWrapper(
    formik: FormikProps<Values>,
    e: React.FormEvent<HTMLFormElement>
  ) {
    e.preventDefault()
    console.log('entered submit wrapper')
    // only want to save data if something has changed
    if (formik.dirty || formIsDirty) {
      console.log('handle submit')
      formik.handleSubmit(e)
    } else {
      console.log('wrapper next page edit')
      nextPage('read-only')
    }
  }

  async function onSubmit(
    values: Values,
    { setSubmitting }: FormikHelpers<Values>
  ): Promise<void> {
    const input = {
      account: {
        id:
          authentication &&
          authentication?.account &&
          authentication?.account.id
            ? authentication?.account.id
            : 0,
        givenName: values.firstName,
        middleName: values.middleName,
        familyName: values.lastName,
        suffix: values.suffix,
        homePhone: values.homePhone,
        mobilePhone: values.mobilePhone,
        workPhone: values.workPhone,
        userName: values.emailAddress,
        emailAddress: values.emailAddress,
        street: values.mailingAddress,
        city: values.city,
        state: values.selectState,
        zip: values.zipCode,
        ssn: '',
        accountCreationStep:
          authentication &&
          authentication?.account &&
          authentication?.account.accountCreationStep
            ? authentication?.account.accountCreationStep
            : 0,
        contactType: getContactTypeValueFromArray(values.contactType)
      }
    }
    const updateAccountResult = await updateAccountHelper(input)
    if (updateAccountResult?.success) {
      //  console.log('account verify account details saved ...')
      const empty = getEmptyUserProfile()
      dispatch(setUserProfile(empty))
      nextPage('read-only')
    } else {
      //   console.log('error saving account')
      setSubmitErrorMessage(
        updateAccountResult?.error
          ? updateAccountResult?.error
          : 'unknown error happended'
      )
      setSubmitting(false)
    }
  }

  const onCancel = () => {
    previousPage('read-only')
  }

  function onClickCommon(formik: FormikProps<Values>): void {
    if (formik.dirty) {
      const newUserProfile = getEmptyUserProfile()
      newUserProfile.isActive = true
      newUserProfile.givenName = formik.values.firstName
      newUserProfile.familyName = formik.values.lastName
      newUserProfile.middleName = formik.values.middleName
      newUserProfile.suffix = formik.values.suffix
      newUserProfile.homePhone = formik.values.homePhone
      newUserProfile.mobilePhone = formik.values.mobilePhone
      newUserProfile.workPhone = formik.values.workPhone
      newUserProfile.emailAddress = formik.values.emailAddress
      newUserProfile.street = formik.values.mailingAddress
      newUserProfile.zip = formik.values.zipCode
      newUserProfile.city = formik.values.city
      newUserProfile.state = formik.values.selectState
      newUserProfile.contactType = getContactTypeValueFromArray(
        formik.values.contactType
      )
      dispatch(setUserProfile(newUserProfile))
      console.log('onResetPasswordClick dispatch', newUserProfile)
    } else {
      console.log('onResetPasswordClick form is not dirty')
    }
  }

  function onResetPasswordClick(formik: FormikProps<Values>): void {
    onClickCommon(formik)
    nextPage('reset')
  }

  function onChangeMobilePhoneClick(formik: FormikProps<Values>): void {
    onClickCommon(formik)
    nextPage('changeMobilePhone')
  }

  function onChangeEmailAddressClick(formik: FormikProps<Values>): void {
    onClickCommon(formik)
    nextPage('changeEmailAddress')
  }

  if (loading) return <div>Loading ...</div>

  return (
    <Formik
      initialValues={initialVals}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {(formik) => {
        console.log('Formik props', formik)

        return (
          <Form
            className='form-container user-profile-edit-form'
            onSubmit={(e) => onSubmitWrapper(formik, e)}
          >
            <div id='newAccountVerifyAccountDetails'>
              <div className='row'>
                <div className='form-group col-12'>
                  <label htmlFor='firstName'>First Name</label>
                  <Field
                    id='firstName'
                    name='firstName'
                    placeholder=''
                    className={
                      formik.errors.firstName
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                    autoFocus
                  />
                  <ErrorMessage
                    name='firstName'
                    render={(msg) => <div className='text-danger'>{msg}</div>}
                  />
                </div>
                <div className='form-group col-12'>
                  <label htmlFor='lastName'>Last Name</label>
                  <Field
                    type='text'
                    id='lastName'
                    name='lastName'
                    placeholder=''
                    className={
                      formik.errors.lastName
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                  />
                  <ErrorMessage
                    name='lastName'
                    render={(msg) => <div className='text-danger'>{msg}</div>}
                  />
                </div>
              </div>

              <div className='row'>
                <div className='form-group col-12'>
                  <label htmlFor='middleName'>Middle Name</label>
                  <Field
                    type='text'
                    id='middleName'
                    name='middleName'
                    placeholder='Optional'
                    className='form-control'
                  />
                  <ErrorMessage
                    name='middleName'
                    render={(msg) => <div className='text-danger'>{msg}</div>}
                  />
                </div>

                <div className='form-group col-12'>
                  <label htmlFor='suffix'>Suffix</label>
                  <Field
                    type='text'
                    id='suffix'
                    name='suffix'
                    placeholder='Optional'
                    className='form-control'
                  />
                  <ErrorMessage
                    name='suffix'
                    render={(msg) => <div className='text-danger'>{msg}</div>}
                  />
                </div>
              </div>

              <div className='row'>
                <div className='form-group col-12'>
                  <label htmlFor='homePhone'>Home Phone</label>
                  <Field
                    type='text'
                    id='homePhone'
                    name='homePhone'
                    placeholder=''
                    className={
                      formik.errors.homePhone
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                  />
                  <ErrorMessage
                    name='homePhone'
                    render={(msg) => <div className='text-danger'>{msg}</div>}
                  />
                </div>

                <div className='form-group col-12'>
                  <label className='float-left' htmlFor='mobilePhone'>
                    Mobile Phone
                  </label>
                  <button
                    onClick={() => onChangeMobilePhoneClick(formik)}
                    type='button'
                    className='btn btn-link float-right'
                  >
                    change mobile phone
                  </button>
                  <Field
                    type='text'
                    id='mobilePhone'
                    name='mobilePhone'
                    placeholder='Optional'
                    className={
                      formik.errors.mobilePhone
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                    disabled={true}
                  />
                  <ErrorMessage
                    name='mobilePhone'
                    render={(msg) => <div className='text-danger'>{msg}</div>}
                  />
                </div>
              </div>

              <div className='row'>
                <div className='form-group col-12'>
                  <label htmlFor='workPhone'>Work Phone</label>
                  <Field
                    type='text'
                    id='workPhone'
                    name='workPhone'
                    placeholder='Optional'
                    className={
                      formik.errors.workPhone
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                  />
                  <ErrorMessage
                    name='workPhone'
                    render={(msg) => <div className='text-danger'>{msg}</div>}
                  />
                </div>

                <div className='form-group col-12'>
                  <label className='float-left' htmlFor='emailAddress'>
                    Email Address (User ID)
                  </label>
                  <button
                    onClick={() => onChangeEmailAddressClick(formik)}
                    type='button'
                    className='btn btn-link float-right'
                  >
                    change email address
                  </button>
                  <Field
                    type='email'
                    id='emailAddress'
                    name='emailAddress'
                    placeholder=''
                    className={
                      formik.errors.emailAddress
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                    disabled={true}
                  />
                  <ErrorMessage
                    name='emailAddress'
                    render={(msg) => <div className='text-danger'>{msg}</div>}
                  />
                </div>
              </div>

              <div className='row'>
                <div className='form-group col-12'>
                  <label htmlFor='mailingAddress'>Mailing Address</label>
                  <Field
                    as='textarea'
                    id='mailingAddress'
                    name='mailingAddress'
                    placeholder='Optional'
                    className='form-control'
                  />
                  <ErrorMessage
                    name='mailingAddress'
                    render={(msg) => <div className='text-danger'>{msg}</div>}
                  />
                </div>

                <div className='form-group col-12'>
                  <label htmlFor='zipCode'>Zip Code</label>
                  <Field
                    type='text'
                    id='zipCode'
                    name='zipCode'
                    placeholder='Optional'
                    className='form-control'
                  />
                  <ErrorMessage
                    name='zipCode'
                    render={(msg) => <div className='text-danger'>{msg}</div>}
                  />
                </div>
              </div>

              <div className='row'>
                <div className='form-group col-12'>
                  <label htmlFor='city'>City</label>
                  <Field
                    type='text'
                    id='city'
                    name='city'
                    placeholder='Optional'
                    className='form-control'
                  />
                  <ErrorMessage
                    name='city'
                    render={(msg) => <div className='text-danger'>{msg}</div>}
                  />
                </div>
                <Select
                  selectStyling='form-group col-12'
                  fieldStyling={
                    formik.errors.selectState
                      ? 'form-control is-invalid'
                      : 'form-control'
                  }
                  label='State'
                  name='selectState'
                  options={dropdownStateOptions}
                >
                  <ErrorMessage
                    name='selectState'
                    render={(msg) => <div className='text-danger'>{msg}</div>}
                  />
                </Select>
              </div>
            </div>

            <div className='row'>
              <div className='form-group col-12'>
                <label className='float-left' htmlFor='password'>
                  Password
                </label>
                <button
                  onClick={() => onResetPasswordClick(formik)}
                  type='button'
                  className='btn btn-link float-right'
                >
                  change password
                </button>
                <Field
                  type='password'
                  id='password'
                  name='password'
                  placeholder='Optional'
                  className='form-control'
                  disabled={true}
                />
              </div>
            </div>

            <div className='row notification-checkbox-container'>
              <div className='form-group col-12 col-sm-12 col-md mb-0'>
                <label>Send Notifications To</label>
                <div
                  role='group'
                  className='notification-checkbox-group'
                  aria-labelledby='checkbox-group'
                >
                  <label>
                    <Field
                      type='checkbox'
                      name='contactType'
                      value='Email'
                      className={
                        formik.errors.contactType ? 'invalid-checkbox' : ''
                      }
                    />
                    <div className='checkbox-label'>Email</div>
                  </label>
                  <br />
                  <label>
                    <Field
                      type='checkbox'
                      name='contactType'
                      value='Text'
                      className={
                        formik.errors.contactType ? 'invalid-checkbox' : ''
                      }
                    />
                    <div className='checkbox-label'>Text</div>
                  </label>
                </div>
                <ErrorMessage
                  name='contactType'
                  render={(msg) => (
                    <div
                      className='text-danger'
                      data-testid='create-account-verify-contact-info-contact-type-field-error'
                    >
                      {msg}
                    </div>
                  )}
                />
              </div>
            </div>

            {submitErrorMessage && (
              <div className='text-danger'>{submitErrorMessage}</div>
            )}

            <ButtonsContainer className='p-0'>
              <TwoBtnsContainer>
                <Button
                  variant='outline-secondary'
                  onClick={onCancel}
                  className='btn-cancel mt-0'
                >
                  Cancel
                </Button>
                <Button type='submit' className='btn btn-submit w-100'>
                  Save Changes
                </Button>
              </TwoBtnsContainer>
            </ButtonsContainer>
          </Form>
        )
      }}
    </Formik>
  )
}

export default UserProfileEditForm
